/**
 * Cookie preferences + Google Consent Mode bridge.
 *
 * Policy (2026-08-04): analytics and advertising storage default to DENIED in
 * `index.html` (Consent Mode v2, `wait_for_update`). Tags themselves still
 * load so tag detection works, but they may not write analytics/ad cookies
 * until the visitor accepts. Accepting grants every storage type; rejecting
 * keeps analytics/ad storage denied and prevents GA4 / Meta Pixel / AdSense
 * from being injected at all.
 *
 * This module is the single source of truth for that decision. It also
 * exposes `openConsentSettings()` so the footer and the settings page can
 * re-open the chooser at any time, as promised in the privacy policy.
 */

export const CONSENT_STORAGE_KEY = "statlign_consent_v1";

export type ConsentStatus = "accepted" | "rejected";

export interface ConsentRecord {
  status: ConsentStatus;
  /** ISO timestamp of the decision. */
  updatedAt: string;
  /** Schema version so we can re-prompt if policy materially changes. */
  version: 1;
}

/** Fired on window whenever consent changes so listeners (e.g. analytics) react. */
export const CONSENT_EVENT = "statlign:consent-change";
/** Fired on window when the visitor asks to review their cookie choices. */
export const CONSENT_OPEN_EVENT = "statlign:consent-open";

function gtagConsentUpdate(granted: boolean) {
  if (typeof window === "undefined") return;
  const value = granted ? "granted" : "denied";
  const payload = {
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
    analytics_storage: value,
    // Essential storage is always available — the app cannot run without it.
    functionality_storage: "granted",
    personalization_storage: value,
    security_storage: "granted",
  };
  const w = window as typeof window & { gtag?: (...args: unknown[]) => void };
  if (typeof w.gtag === "function") {
    w.gtag("consent", "update", payload);
  } else {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ 0: "consent", 1: "update", 2: payload } as unknown as Record<string, unknown>);
  }
}


/** Read the stored consent decision, or null if the visitor hasn't chosen yet. */
export function getConsent(): ConsentRecord | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentRecord;
    if (parsed && (parsed.status === "accepted" || parsed.status === "rejected")) {
      return parsed;
    }
  } catch {
    /* ignore malformed storage */
  }
  return null;
}

export function hasConsentDecision(): boolean {
  return getConsent() !== null;
}

/**
 * Measurement model (2026-09-08): Statlign measures every visit, in every
 * region, from the first page view. The stored record only ever records an
 * OPT-OUT — the absence of a decision never blocks measurement, which is why
 * EU/US traffic was previously missing from reports entirely.
 */
export function hasAnalyticsConsent(): boolean {
  return getConsent()?.status !== "rejected";
}

/** Advertising/remarketing storage follows the same opt-out model. */
export function hasAdvertisingConsent(): boolean {
  return getConsent()?.status !== "rejected";
}




/** Persist a decision, update Consent Mode, and notify listeners. */
export function setConsent(status: ConsentStatus): ConsentRecord {
  const record: ConsentRecord = {
    status,
    updatedAt: new Date().toISOString(),
    version: 1,
  };
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
  } catch {
    /* storage may be unavailable (private mode) — consent still applies for session */
  }
  gtagConsentUpdate(status === "accepted");
  // Signal GTM with a semantic event so tags/triggers can react immediately.
  if (typeof window !== "undefined") {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: "consent_update", consent_status: status });
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: record }));
  }
  return record;
}

/**
 * Re-open the cookie chooser. Called from the "Cookie settings" link in the
 * footer and from the privacy controls on the settings page.
 */
export function openConsentSettings() {
  if (typeof window === "undefined") return;
  const w = window as typeof window & {
    googlefc?: { showRevocationMessage?: () => void };
  };
  if (typeof w.googlefc?.showRevocationMessage === "function") {
    w.googlefc.showRevocationMessage();
    return;
  }
  window.dispatchEvent(new CustomEvent(CONSENT_OPEN_EVENT));
}
